import { useCallback } from "react";
import useSWR from "swr";
import { vceFirestore, hvoFirestore } from "../../configs/FirebaseConfig";

/**
 * Fetcher function for products - fetches from both databases
 */
const fetchProductsFromFirestore = async () => {
  // Fetch VCE products
  const vceProductsSnapshot = await vceFirestore.collection("products").get();
  const vceProducts = vceProductsSnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    shopSource: "VCE",
  }));

  // Fetch HVO products
  const hvoProductsSnapshot = await hvoFirestore.collection("products").get();
  const hvoProducts = hvoProductsSnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    shopSource: "HVO",
  }));

  return [...vceProducts, ...hvoProducts];
};

/**
 * Hook to fetch and manage products from both VCE and HVO Firestore databases with SWR caching
 * @param {Object} options - Configuration options
 * @param {number} options.refreshInterval - Auto-refresh interval in ms (default: 0 - no auto refresh)
 * @returns {Object} - Products data and utility functions
 */
export const useProducts = (options = {}) => {
  const { refreshInterval = 0 } = options;

  // Use SWR for data fetching with caching
  const { data: products, error, isLoading, mutate } = useSWR(
    "products",
    fetchProductsFromFirestore,
    {
      refreshInterval,
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 10000, // Dedupe requests within 10 seconds
    }
  );

  /**
   * Manually refresh products
   * @returns {Promise<Array>} - Updated products
   */
  const refreshProducts = useCallback(() => {
    return mutate();
  }, [mutate]);

  /**
   * Get products filtered by shop source
   * @param {string} shopSource - "VCE" or "HVO"
   * @returns {Array} - Filtered products
   */
  const getProductsByShop = useCallback(
    (shopSource) => {
      if (!shopSource || !products) return products || [];
      return products.filter((product) => product.shopSource === shopSource);
    },
    [products],
  );

  /**
   * Find a specific product by ID
   * @param {string} productId - The product ID to search for
   * @returns {Object|null} - The product object or null if not found
   */
  const findProductById = useCallback(
    (productId) => {
      if (!productId || !products) return null;
      return products.find((product) => product.id === productId) || null;
    },
    [products],
  );

  /**
   * Get products that have productItems (combo products)
   * @returns {Array} - Products with productItems
   */
  const getComboProducts = useCallback(() => {
    if (!products) return [];
    return products.filter((product) => product.productItems && product.productItems.length > 0);
  }, [products]);

  /**
   * Get frozen products
   * @returns {Array} - Frozen products
   */
  const getFrozenProducts = useCallback(() => {
    if (!products) return [];
    return products.filter((product) => product.frozen === true);
  }, [products]);

  return {
    products: products || [],
    isLoading,
    error,
    refreshProducts,
    getProductsByShop,
    findProductById,
    getComboProducts,
    getFrozenProducts,
  };
};
